import React, { useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify"; // For HTML sanitization
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import "./TeamsChat.css"; // Your styles
import { ArrowUpRight16Filled } from "@fluentui/react-icons";

function TeamsChat({ projectData }) {
  const { instance, accounts } = useMsal();

  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const isCreatingChatRef = useRef(false); // persistent flag

  useEffect(() => {
    if (accounts.length > 0) {
      // Acquire token silently
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then((response) => {
          // Fetch user’s chats
          fetchChats(response.accessToken);
        })
        .catch((error) => {
          console.error("Error acquiring token for Teams chat:", error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, instance]);

  // Fetch all chats belonging to the user
  const fetchChats = async (accessToken) => {
    try {
      const res = await fetch("https://graph.microsoft.com/v1.0/me/chats", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) {
        const errorData = await res.json();
        console.error("Failed to fetch chats:", errorData);
        return;
      }
      const data = await res.json();
      // Filter chats based on the project opportunityNo
      const filteredChats = (data.value || []).filter((chat) =>
        chat.topic?.includes(projectData.opportunityNo)
      );

      if (filteredChats.length > 0) {
        setSelectedChat(filteredChats[0]);
        fetchChatMessages(accessToken, filteredChats[0].id);
      } else if (!isCreatingChatRef.current) {
        // If no chat exists and we're not already creating one
        isCreatingChatRef.current = true;
        const newChat = await createChatGroup(accessToken);
        isCreatingChatRef.current = false;
        if (newChat) {
          setSelectedChat(newChat);
          fetchChatMessages(accessToken, newChat.id);
          setChats((prevChats) => [...prevChats, newChat]);
        } else {
          setSelectedChat(null);
          setMessages([]);
        }
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  };

  // Create a new chat group using Graph API
  const createChatGroup = async (accessToken) => {
    try {
      // Replace with the actual user ID of the current user and any other members.
      // Here, we're adding the current user as an owner.
      const currentUserId = accounts[0]?.localAccountId; // Adjust if necessary
      console.log(currentUserId,'usercurrentid');

      const payload = {
        chatType: "group",
        topic: `PN - ${projectData.projectName} - ${projectData.opportunityNo}`,
        members: [
          {
            "@odata.type": "#microsoft.graph.aadUserConversationMember",
            roles: ["owner"],
            // Bind to the current user (adjust this binding as needed)
            //aimleap developers user id hardcoded 159773a7-e42b-4a80-a1c8-566e1b5a561c
            "user@odata.bind": `https://graph.microsoft.com/v1.0/users/159773a7-e42b-4a80-a1c8-566e1b5a561c`,
          },
          // Add additional members if needed.
        ],
      };

      const res = await fetch("https://graph.microsoft.com/v1.0/chats", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errorData = await res.json();
        console.error("Failed to create chat group:", errorData);
        return null;
      }
      const newChat = await res.json();
      return newChat;
    } catch (error) {
      console.error("Error creating chat group:", error);
      return null;
    }
  };

  // Fetch messages from the selected chat
  const fetchChatMessages = async (accessToken, chatId) => {
    try {
      const res = await fetch(
        `https://graph.microsoft.com/v1.0/chats/${chatId}/messages`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      if (!res.ok) {
        const errorData = await res.json();
        console.error("Failed to fetch messages:", errorData);
        return;
      }
      const data = await res.json();
      const transformed = (data.value || [])
        .filter((msg) => msg.from?.user?.displayName) // Exclude messages without a displayName
        .map((msg) => ({
          id: msg.id,
          sender: msg.from.user.displayName,
          createdDateTime: msg.createdDateTime,
          content: msg.body?.content || "",
        }))
        .slice(0, 5); // Limit to the latest 5 messages

      setMessages(transformed);
    } catch (error) {
      console.error("Error fetching chat messages:", error);
    }
  };

  return (
    <section className="teams-chat">
      <div className="chat-header">
        <h2 className="chat-title">Teams Chat</h2>
        {selectedChat && (
          <a
            className="view-all-btn"
            href={selectedChat.webUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View All
            <ArrowUpRight16Filled />
          </a>
        )}
      </div>

      {selectedChat ? (
        <>
          <div className="chat-group-name">
            <h3>Group Name: {selectedChat.topic || "Unnamed Chat"}</h3>
          </div>
          <div className="chat-messages">
            {messages.map((msg) => (
              <div key={msg.id} className="chat-message">
                <div className="message-header">
                  <div className="sender-info">
                    <div className="avatar">
                      {msg.sender
                        .split(" ")
                        .map((word) => word[0])
                        .join("")
                        .toUpperCase()}
                    </div>
                    <div className="sender-name">{msg.sender}</div>
                  </div>
                  <div className="message-time">
                    <span>
                      {new Date(msg.createdDateTime).toLocaleDateString([], {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}{" "}
                      {new Date(msg.createdDateTime).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
                <div
                  className="message-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(msg.content),
                  }}
                />
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="no-chat-found">
          <p>No chat found for the selected project.</p>
        </div>
      )}
    </section>
  );
}

export default TeamsChat;
